import { Link, useSearchParams } from 'react-router-dom'
import { T } from '@/lib/theme'
import { FilterPill } from '@/components/ui'

/// The frame every detail page shares: breadcrumb, back arrow, identity, a right-hand meta strip,
/// and a tab rail. Markets, Multiply and (once this has survived both) Vaults all sit inside it.
///
/// **Tabs are URL state, not component state.** App.jsx's own comment records the reasoning behind
/// routing the top-level nav - "a tab click is a real navigation (back/forward, bookmarks,
/// shareable links) rather than a scroll-to-anchor jump" - and the same argument applies one level
/// down. Someone linking a friend to a loop's FAQ should be able to. Stored in a `?tab=` search
/// param rather than a path segment so the page's own address stays the canonical one and a
/// missing/unknown tab falls back cleanly to the first.
export function DetailPageShell({
  backTo,
  backLabel,
  breadcrumb = [],
  glyph,
  title,
  // Identity size override. Defaults to the shared heading scale; the Vaults page passes a larger
  // value so its pair glyph + name carry the weight the Markets table's asset column now does.
  titleSize,
  badges = [],
  meta = [],
  tabs = [],
  children,
}) {
  const [params, setParams] = useSearchParams()
  const requested = params.get('tab')
  const active = tabs.find((t) => t.id === requested)?.id ?? tabs[0]?.id

  function selectTab(id) {
    const next = new URLSearchParams(params)
    if (id === tabs[0]?.id) next.delete('tab')
    else next.set('tab', id)
    // `replace` so a burst of tab clicks does not bury the page someone arrived from under a stack
    // of history entries they have to click back through.
    setParams(next, { replace: true })
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {breadcrumb.length > 0 && (
        <nav style={crumbBar}>
          {breadcrumb.map((c, i) => (
            <span key={`${c.label}-${i}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              {i > 0 && <span style={{ color: T.textDim }}>›</span>}
              {c.to ? <Link to={c.to} style={crumbLink}>{c.label}</Link> : <span style={crumbCurrent}>{c.label}</span>}
            </span>
          ))}
        </nav>
      )}

      <div style={headerRow}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, minWidth: 0 }}>
          {backTo && (
            <Link to={backTo} aria-label={backLabel ?? 'Back'} style={backBtn}>←</Link>
          )}
          {glyph}
          <h1 style={{ ...titleStyle, fontSize: titleSize ?? titleStyle.fontSize }}>{title}</h1>
          {badges.map((b) => (
            <span key={b.label} style={badgeStyle(b.color ?? T.accent)}>{b.label}</span>
          ))}
        </div>

        {meta.length > 0 && (
          <div style={metaStrip}>
            {meta.map((m, i) => (
              <span key={m.label} style={{ display: 'inline-flex', alignItems: 'baseline', gap: 8 }}>
                {i > 0 && <span style={metaDivider} />}
                <span style={metaLabel}>{m.label}</span>
                <span className="mono" style={{ ...metaValue, color: m.color ?? T.text }}>{m.value}</span>
              </span>
            ))}
          </div>
        )}
      </div>

      {tabs.length > 0 && (
        <div style={tabRail}>
          {tabs.map((t) => (
            <button key={t.id} onClick={() => selectTab(t.id)} style={tabBtn(t.id === active)}>
              {t.label}
            </button>
          ))}
        </div>
      )}

      <div>{typeof children === 'function' ? children(active) : children}</div>
    </div>
  )
}

/// Sub-tabs INSIDE a tab (the reference's Loop Info / Advanced Info, Position Overview / Analytics).
/// Pill-shaped rather than underlined so the two levels stay visually distinct - an underline at
/// both levels reads as one confused rail.
export function SubTabs({ tabs, active, onSelect, style = {} }) {
  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', ...style }}>
      {tabs.map((t) => (
        <FilterPill size="sm" key={t.id} active={t.id === active} onClick={() => onSelect(t.id)}>
          {t.label}
        </FilterPill>
      ))}
    </div>
  )
}

const crumbBar = {
  display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
  fontFamily: T.fontM, fontSize: T.fs.body,
}
const crumbLink = { color: T.textDim, textDecoration: 'none' }
const crumbCurrent = { color: T.textMuted }

const headerRow = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  gap: 20, flexWrap: 'wrap',
}
const backBtn = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  width: 38, height: 38, flexShrink: 0, borderRadius: T.r.md,
  border: `1px solid ${T.border}`, background: T.bg2,
  color: T.textMuted, textDecoration: 'none', fontSize: 16,
}
const titleStyle = {
  margin: 0, color: T.text, fontFamily: T.fontD, fontSize: T.fs.heading,
  fontWeight: 700, letterSpacing: '-0.01em',
}
function badgeStyle(color) {
  return {
    padding: '4px 10px', borderRadius: T.r.pill, border: `1px solid ${color}66`,
    background: `${color}18`, color, fontFamily: T.fontM, fontSize: T.fs.caption,
    fontWeight: 700, whiteSpace: 'nowrap',
  }
}

const metaStrip = { display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }
const metaDivider = { width: 1, height: 16, background: T.border, marginRight: 6 }
const metaLabel = { color: T.textDim, fontFamily: T.fontM, fontSize: T.fs.caption }
const metaValue = { fontFamily: T.fontN, fontSize: T.fs.body, fontWeight: 700 }

const tabRail = {
  display: 'flex', gap: 4, borderBottom: `1px solid ${T.border}`, flexWrap: 'wrap',
}
function tabBtn(active) {
  return {
    padding: '10px 16px', background: 'none', cursor: 'pointer',
    border: 'none', borderBottom: `2px solid ${active ? T.accent : 'transparent'}`,
    color: active ? T.text : T.textDim,
    fontFamily: T.fontM, fontSize: T.fs.ui, fontWeight: 700, marginBottom: -1,
  }
}
